import { useState, useRef, useEffect } from 'react';
import {
  loadVoiceProfile, saveVoiceProfile, addBinsToSum, sumToMeanVector,
  cosineSimilarity, blendEMA, momentumForAdaptationCount,
  DEFAULT_VOICE_MATCH_THRESHOLD, VOICE_ENROLL_MS, MIN_FRAMES_FOR_MATCH,
  buildEnhancedFrame, EnhancedEmbeddingAccumulator,
  computeAdaptiveThreshold, recordMatchScore, loadMatchHistory,
} from '../voiceProfile';
import { cannotReachMessage } from '../apiBase';
import { ARIA_PREFERRED_NAME_KEY } from '../ariaConstants';
import { classifyWakeWord, stripWakeWord, isLikelyFollowUp, isInterruptCommand } from '../wakeWordClassifier';

// Does this transcript read like a FINISHED sentence? Whisper punctuates, so
// a trailing connector ("and.", "so,") still means mid-thought — the period is
// whisper's, not the speaker's. Terminal ?/! is a real stop.
const INCOMPLETE_TAIL_RE = /\b(and|but|or|so|because|cause|to|for|with|of|in|on|at|the|a|an|my|your|their|his|her|its|our|i|i'm|im|is|are|was|were|can|could|would|should|will|need|want|like|about|from|by|as|if|when|while|that|which|then|than|also|plus|um|uh|uhh|umm|er)[\s.,]*$/i;
export function readsUnfinished(t) {
  const s = String(t || '').trim();
  if (!s) return false;
  if (/[?!]$/.test(s)) return false;
  if (/,$/.test(s)) return true;
  return INCOMPLETE_TAIL_RE.test(s);
}

// ── Constants ────────────────────────────────────────────────────────────────
// Was 1800ms (waited for server-side OpenAI Realtime VAD at 1500ms). We've
// since migrated transcription to local Whisper with smart-EOU (~241ms),
// so the client-side VAD silence wait is now the dominant delay. Dropped
// to 500ms for snappy turn-taking — and Braeden got CUT OFF mid-sentence in
// real testing (8/12: "she cuts the user off before they are done talking").
// A natural mid-thought breath is 600-800ms; 900ms is the assistant-standard
// pause window. The +400ms per turn is the price of not interrupting people.
const VAD_SILENCE_MS = 900;
// ── 900ms IS STILL CUTTING HIM OFF (Braeden 9/5) ─────────────────────────
//
// "sometimes she will try to process the whisper before im done talking,
//  which means the whisper needs to make sure its processing the whole thing"
//
// The 900 above was set from an assumption — "a natural mid-thought breath is
// 600-800ms" — and the ear audit on 9/1 then MEASURED it: real mid-sentence
// pauses run to 1600ms, and the end-of-utterance timer was cutting a third of
// all sentences in half. 900 was never going to be enough for a sentence with
// a think in the middle of it.
//
// Waiting 1600ms on every turn would put 700ms back onto "yes" and "the blue
// one", which is the latency this number has been fighting since it was 1800.
// It does not have to be one number: nobody pauses mid-thought inside a
// two-word answer. So the wait depends on whether they are mid-SENTENCE —
// short utterance keeps the snappy 900, anything long enough to be an actual
// sentence gets the measured 1600.
const VAD_SILENCE_LONG_MS = 1600;
// Past this much speech they are explaining something, not answering yes/no.
const VAD_MIDSENTENCE_AFTER_MS = 1500;
const VAD_MIN_UTTERANCE_MS = 400;
// 8000ms was too short for someone telling a story or thinking out loud —
// the system would force end-of-utterance mid-thought. 30s upper bound
// is more than enough for normal speech; truly stuck users get cut after.
const VAD_MAX_UTTERANCE_MS = 30000;
const VAD_SPEECH_START_MS = 120;
// Lowered from 0.002 → 0.0012. The user's idle rms in real
// environments was hovering at 0.00206 (your mic / room noise). With
// threshold at 0.002, normal-volume speech only just barely tripped
// the gate; quieter speech was missed entirely. 0.0012 catches
// quieter voices without picking up ambient hum (which is in the
// 0.0005-0.0009 range on most macs).
const VAD_START_RMS = 0.0012;
const VAD_DURING_TTS_RMS = 0.08; // raised from 0.045 — was letting Aria's own voice retrigger VAD on loud speakers
const VAD_END_RMS = 0.003;
const VAD_COOLDOWN_MS = 150;
const TTS_TAIL_GATE_MS = 600;
const VOICE_GATE_EARLY_FRAMES = 5;
const MIC_SPEAKING_RMS_THRESHOLD = 0.006;
const WAKE_WORD = 'aria';
const REQUIRE_WAKE_WORD_UNTIL_VOICE_ENROLLED = false;
const WAKE_WORD_VARIANTS = ['aria', 'area', 'arya', 'aria', 'ariel', 'hey aria', 'ok aria', 'yo aria'];

const ARIA_USER_ID_KEY = 'aria_user_id';
const VOICE_ONBOARDING_DONE_KEY = 'aria_voice_onboarding_done';
const ARIA_PREFERRED_NAME_BACKUP_KEY = 'aria_preferred_name_backup';

const SpeechRecognitionAPI = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

// ── Module-level helpers ─────────────────────────────────────────────────────

function computeMicRms(analyser) {
  const buf = new Uint8Array(analyser.fftSize);
  analyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / buf.length);
}

function analyzeSpeechSpectrum(analyser, sampleRate) {
  const binCount = analyser.frequencyBinCount;
  const freqData = new Uint8Array(binCount);
  analyser.getByteFrequencyData(freqData);
  const hzPerBin = sampleRate / (binCount * 2);
  const speechLow = Math.floor(300 / hzPerBin);
  const speechHigh = Math.ceil(3400 / hzPerBin);
  const subBassEnd = Math.floor(120 / hzPerBin);
  const highEnd = Math.ceil(6000 / hzPerBin);
  let speechEnergy = 0;
  let totalEnergy = 0;
  let subBassEnergy = 0;
  let highEnergy = 0;
  let speechBins = 0;
  let weightedFreqSum = 0;
  let logSum = 0;
  let arithmeticSum = 0;
  let countNonZero = 0;
  for (let i = 1; i < binCount; i++) {
    const val = freqData[i];
    const energy = val * val;
    totalEnergy += energy;
    weightedFreqSum += energy * i;
    if (i >= speechLow && i <= speechHigh) { speechEnergy += energy; speechBins++; }
    if (i < subBassEnd) subBassEnergy += energy;
    if (i > highEnd) highEnergy += energy;
    if (val > 0) { logSum += Math.log(val); arithmeticSum += val; countNonZero++; }
  }
  if (totalEnergy < 1) {
    return { isSpeech: false, speechRatio: 0, spectralFlatness: 1, centroid: 0 };
  }
  const speechRatio = speechEnergy / totalEnergy;
  const centroidBin = weightedFreqSum / totalEnergy;
  const centroid = centroidBin * hzPerBin;
  let spectralFlatness = 1;
  if (countNonZero > 10) {
    const geometricMean = Math.exp(logSum / countNonZero);
    const arithmeticMean = arithmeticSum / countNonZero;
    spectralFlatness = arithmeticMean > 0 ? geometricMean / arithmeticMean : 1;
  }
  const subBassRatio = subBassEnergy / totalEnergy;
  const highRatio = highEnergy / totalEnergy;
  // Reject keyboard / typing transients: a keystroke is a short BROADBAND click —
  // energy spread across the whole spectrum (low speechRatio), noise-like (high
  // spectral flatness), a high centroid, and lots of energy above 6 kHz. Voiced
  // speech is the opposite: energy concentrated in 300–3400 Hz, harmonic (low
  // flatness), centroid in the mid band, and it rolls off up high. These tighter
  // thresholds keep real speech while dropping clicks so she ignores typing.
  const isSpeech =
    speechRatio > 0.38 &&
    centroid > 400 &&
    centroid < 3300 &&
    spectralFlatness < 0.58 &&
    subBassRatio < 0.35 &&
    highRatio < 0.28;
  return { isSpeech, speechRatio, spectralFlatness, centroid: Math.round(centroid), subBassRatio, highRatio };
}

function pickMediaRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) return 'audio/ogg;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/ogg')) return 'audio/ogg';
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
  if (MediaRecorder.isTypeSupported('audio/aac')) return 'audio/aac';
  return '';
}

function cleanNameFromRaw(raw) {
  const t = String(raw || '').trim();
  if (!t) return '';
  let s = t
    .replace(/^i'?m\s+/i, '')
    .replace(/^my name is\s+/i, '')
    .replace(/^this is\s+/i, '')
    .replace(/^it's\s+/i, '')
    .replace(/^call me\s+/i, '')
    .split(/[.,;!?]/)[0]
    .trim()
    .replace(/\s+/g, ' ');
  if (s.length > 48) s = s.slice(0, 48).trim();
  return s;
}

function isLikelyUserName(name) {
  const n = String(name || '').trim();
  if (!n) return false;
  if (!/^[A-Za-z][A-Za-z' -]{0,46}$/.test(n)) return false;
  const words = n.split(/\s+/).filter(Boolean);
  if (words.length < 1 || words.length > 3) return false;
  const lower = n.toLowerCase();
  const banned = /\b(help|what|can|you|with|today|please|need|aria|assistant|hello|hey|good|morning|afternoon|evening)\b/i;
  if (banned.test(lower)) return false;
  return words.every((w) => w.length >= 2 && w.length <= 20);
}

// ── Layer 4: Conversation context helpers ───────────────────────────────────

const CONTEXT_QUESTION_WINDOW_MS = 15000;   // 15s after Aria asks a question
const CONTEXT_DELEGATION_WINDOW_MS = 10000; // 10s after "let me ask Aria"
const CONTEXT_WAKE_FOLLOW_WINDOW_MS = 3000; // 3s after solo "Aria" callout
const CONTEXT_ACTIVE_TURN_THRESHOLD = 3;    // 3+ turns = active conversation

/**
 * Compute a context boost value that adjusts intent gate thresholds.
 * Positive = more likely to respond, negative = more cautious.
 * Range: -0.2 to +0.4
 */
export function computeContextBoost(ctx, text) {
  const now = Date.now();
  let boost = 0;

  // Aria just asked a question → strong boost for any response
  if (ctx.lastAriaQuestionAt && (now - ctx.lastAriaQuestionAt) < CONTEXT_QUESTION_WINDOW_MS) {
    boost += 0.35;
  }

  // Active multi-turn conversation → lower all thresholds
  if (ctx.conversationTurnCount >= CONTEXT_ACTIVE_TURN_THRESHOLD) {
    boost += 0.2;
  } else if (ctx.conversationTurnCount === 0) {
    // Cold start — be more cautious
    boost -= 0.15;
  }

  // Delegation detected → auto-pass
  if (ctx.pendingDelegation && (now - ctx.pendingDelegationAt) < CONTEXT_DELEGATION_WINDOW_MS) {
    boost += 0.4;
  }

  // Solo wake word recently → next utterance is likely directed
  if (ctx.pendingWakeWordAt && (now - ctx.pendingWakeWordAt) < CONTEXT_WAKE_FOLLOW_WINDOW_MS) {
    boost += 0.35;
  }

  // Aria recently responded — engagement window
  if (ctx.lastAriaResponseAt && (now - ctx.lastAriaResponseAt) < 45000) {
    // Check if utterance looks like a follow-up
    if (isLikelyFollowUp(text)) {
      boost += 0.25;
    }
  }

  // Topic continuity — if recent topics overlap with current text
  if (ctx.recentTopics.length > 0 && text) {
    const lower = text.toLowerCase();
    const topicMatch = ctx.recentTopics.some(t => lower.includes(t));
    if (topicMatch) boost += 0.15;
  }

  return Math.max(-0.2, Math.min(0.4, boost));
}

/**
 * Extract simple topic keywords from text for continuity tracking.
 */
export function extractTopicKeywords(text) {
  if (!text) return [];
  const stop = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
    'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may',
    'might', 'can', 'shall', 'must', 'to', 'of', 'in', 'for', 'on', 'with', 'at', 'by',
    'from', 'as', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'between',
    'and', 'but', 'or', 'not', 'no', 'yes', 'so', 'if', 'then', 'than', 'that', 'this',
    'it', 'its', 'i', 'me', 'my', 'you', 'your', 'we', 'our', 'they', 'them', 'their',
    'he', 'she', 'him', 'her', 'what', 'how', 'why', 'when', 'where', 'which', 'who',
    'aria', 'please', 'just', 'also', 'very', 'really', 'about', 'like', 'some', 'all',
    'any', 'each', 'every', 'much', 'many', 'more', 'most', 'other', 'get', 'got', 'make']);
  return text.toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 3 && !stop.has(w))
    .slice(0, 5);
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export default function useVoiceCapture({
  isDesktopApp, effectiveSection, micOn, setMicOn, voiceEnrolling, introLocked, introLockedRef,
  hasSpokenDesktopIntro, voicePolicy, setVoicePolicy, getAudioConstraints,
  ariaSpeakingRef, ariaAttentionRef, voiceProfileGateRef, ttsPostPlaybackUntilRef,
  awaitingIntroNameRef, proactivePausedRef, preferredNameRef, breakIntervalMsRef,
  micBtnRef, sendMessageWithTextRef,
  transcribeDesktopBlob, queueOrSendDesktopTranscript, stopAriaPlayback,
  expectingAnswerRef,
  lastTranscribeIntentRef,
  onVoiceRead,   // (read) => store for the next turn; mirrors streamingTranscribe onFinal
  micInsecureContextMessage, microphoneDeniedMessage,
  isDev, setInput, showLiveInBubble, unlockAudioForReply,
  syncVoiceProfileGate, resetAttentionTimer,
  voiceError, setVoiceError,
}) {
  // ── State ────────────────────────────────────────────────────────────────
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceListening, setVoiceListening] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [activeMicLabel, setActiveMicLabel] = useState('');
  const [lastMicPeak, setLastMicPeak] = useState(null);
  const [voiceProfileEnrolled, setVoiceProfileEnrolled] = useState(() => !!loadVoiceProfile());
  const [voiceAdaptationCount, setVoiceAdaptationCount] = useState(() => loadVoiceProfile()?.adaptationCount ?? 0);
  const [voiceMatchStrict, setVoiceMatchStrict] = useState(() => {
    const p = loadVoiceProfile();
    return p ? p.strict !== false : true;
  });
  const [voiceEnrollProgress, setVoiceEnrollProgress] = useState(0);
  const [micSpeaking, setMicSpeaking] = useState(false);

  // ── Layer 4: Conversation context state ───────────────────────────────────
  // These track conversation flow to make better "should I respond?" decisions
  const conversationContextRef = useRef({
    lastAriaQuestionAt: 0,      // When Aria last asked a question
    conversationTurnCount: 0,   // Back-and-forth turns in current attentive session
    pendingDelegation: false,   // "let me ask Aria" detected — next utterance auto-passes
    pendingDelegationAt: 0,     // Timestamp of delegation detection
    pendingWakeWordAt: 0,       // Solo "Aria" detected — next utterance within 3s auto-passes
    lastAriaResponseAt: 0,      // When Aria last responded (for engagement window)
    recentTopics: [],           // Keywords from recent conversation for topic continuity
    taskKnown: false,           // Engaged mode: Aria understands task → no name required per turn
  });

  // ── Refs ──────────────────────────────────────────────────────────────────
  const transcribeDesktopBlobRef = useRef(transcribeDesktopBlob);
  const queueOrSendDesktopTranscriptRef = useRef(queueOrSendDesktopTranscript);
  const stopAriaPlaybackRef = useRef(stopAriaPlayback);
  const microphoneDeniedMessageRef = useRef(microphoneDeniedMessage);
  const vadPendingTranscriptionRef = useRef(null);
  const recognitionRef = useRef(null);
  // Last voice utterance actually sent — { text, at }. MUST be a ref, not an
  // effect-local `let`: the recognizer restarts constantly and the effect can
  // re-run, and every new closure got its own `lastSent = 0`. Two overlapping
  // recognizer instances therefore couldn't see each other's sends, so the
  // same utterance went out twice ~0.6s apart and Aria answered it twice —
  // reading as her talking on her own. A ref is shared across all closures.
  const lastVoiceSendRef = useRef({ text: '', at: 0 });
  // ── FULL-SENTENCE DOCTRINE (Braeden 8/12) ─────────────────────────────────
  // "she analyses every word that comes through to make sense of it but
  // doesnt answer until the full sentance is out, no matter what."
  // When a finalized transcript READS unfinished (trailing connector, comma,
  // mid-thought), we HOLD it here instead of sending, keep the mic listening,
  // and stitch the next utterance onto it. A hold auto-sends after 5s of true
  // silence (they really did stop), and an explicit mic-close (PTT release)
  // always sends immediately — closing the mic IS "I'm done".
  const pendingUtteranceRef = useRef({ text: '', at: 0 });
  const pendingSendTimerRef = useRef(null);
  // Synchronous mirror of micOn — lets async callbacks (MediaRecorder.onstop,
  // transcription promises) check the LATEST mute state without waiting for
  // a re-render. Without this, audio captured just before mute still gets
  // transcribed and sent to Aria.
  const micOnRef = useRef(micOn);

  // ── Ref sync effects ─────────────────────────────────────────────────────
  useEffect(() => { transcribeDesktopBlobRef.current = transcribeDesktopBlob; }, [transcribeDesktopBlob]);
  useEffect(() => { queueOrSendDesktopTranscriptRef.current = queueOrSendDesktopTranscript; }, [queueOrSendDesktopTranscript]);
  useEffect(() => { stopAriaPlaybackRef.current = stopAriaPlayback; }, [stopAriaPlayback]);
  useEffect(() => { microphoneDeniedMessageRef.current = microphoneDeniedMessage; }, [microphoneDeniedMessage]);
  // When the mic was last opened. PTT doctrine says the `\` press IS the
  // "I'm talking to you" signal — but that intent decays: a mic left open for
  // minutes picking up Spotify is not the user talking. Used by the phantom-
  // filler gate below to tell a deliberate "hey" (right after opening) from a
  // whisper hallucination (mic idling open).
  const micOpenedAtRef = useRef(0);
  useEffect(() => {
    micOnRef.current = micOn;
    if (micOn) micOpenedAtRef.current = Date.now();
  }, [micOn]);

  // ── VAD capability check ─────────────────────────────────────────────────
  const vadCanRun = typeof MediaRecorder !== 'undefined' && pickMediaRecorderMime() !== null;

  // ── Effect 1: Mic Level Indicator ────────────────────────────────────────
  useEffect(() => {
    if (!micOn || effectiveSection !== 'ask' || !navigator.mediaDevices?.getUserMedia) return;
    let stream = null;
    let ctx = null;
    let rafId = null;
    let lastSpeaking = false;
    let lastUiUpdateAt = 0;
    // Race-safe cleanup flag — if the effect tears down (mute, unmount, etc.)
    // BEFORE getUserMedia() resolves, the .then() handler below still fires
    // with the stream. Without this check the stream leaks and the mic
    // hardware (and its indicator light) stay on forever.
    let cancelled = false;
    navigator.mediaDevices.getUserMedia(getAudioConstraints()).then((s) => {
      if (cancelled) {
        // User muted / effect unmounted while getUserMedia was in flight.
        // Release the stream immediately so the OS mic light turns off.
        try { s.getTracks().forEach((t) => t.stop()); } catch (_) {}
        return;
      }
      stream = s;
      const activeTrack = s.getAudioTracks ? s.getAudioTracks()[0] : null;
      if (activeTrack?.label) setActiveMicLabel(activeTrack.label);
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        ctx = new Ctx();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        let noiseFloor = MIC_SPEAKING_RMS_THRESHOLD * 0.6;
        const FLOOR_ALPHA = 0.997;
        const SPEECH_RATIO = 3.5;
        // ── BARGE-IN state (constitution r16, ratified 7/22) ───────────────
        // "she doesnt stop talking right away... after about 1.5 sec shell
        // stop" — sustained user speech while Aria is talking → she yields.
        let bargeSpeechStart = 0;
        let bargeFired = false;
        const tick = () => {
          if (!stream || stream.getTracks().every((t) => t.readyState !== 'live')) return;
          const rms = computeMicRms(analyser);
          if (rms < noiseFloor * 1.8) {
            noiseFloor = FLOOR_ALPHA * noiseFloor + (1 - FLOOR_ALPHA) * rms;
            noiseFloor = Math.max(0.0015, Math.min(noiseFloor, 0.012));
          }
          const dynamicThreshold = Math.max(MIC_SPEAKING_RMS_THRESHOLD, noiseFloor * SPEECH_RATIO);
          const speaking = rms > dynamicThreshold;
          const btn = micBtnRef.current;
          if (btn) {
            if (speaking) { btn.classList.add('input-mic-btn--live-speech'); }
            else { btn.classList.remove('input-mic-btn--live-speech'); }
          }
          const now = Date.now();
          // ── BARGE-IN core (constitution r16) ─────────────────────────────
          // User keeps talking over Aria for 1.5s → she yields mid-sentence.
          // Their captured speech then flows to the brain as normal (she
          // "expands on it"); if SILENCE follows instead, App.jsx hears the
          // event below and plays the apology line after a grace window.
          if (ariaSpeakingRef.current && speaking) {
            if (!bargeSpeechStart) bargeSpeechStart = now;
            if (!bargeFired && now - bargeSpeechStart >= 1500) {
              bargeFired = true;
              try { stopAriaPlaybackRef.current(); } catch (_) {}
              try { window.dispatchEvent(new CustomEvent('aria:barge-in', { detail: { at: now } })); } catch (_) {}
            }
          } else if (!speaking) {
            bargeSpeechStart = 0;
            bargeFired = false; // overlap cleared — re-arm for the next one
          }
          if (speaking !== lastSpeaking && now - lastUiUpdateAt >= 500) {
            lastSpeaking = speaking;
            lastUiUpdateAt = now;
            setMicSpeaking(speaking);
          }
          rafId = setTimeout(tick, 30);
        };
        tick();
      } catch (_) {}
    }).catch(() => {});
    return () => {
      cancelled = true;
      if (rafId != null) clearTimeout(rafId);
      if (ctx) try { ctx.close(); } catch (_) {}
      if (stream) {
        try { stream.getTracks().forEach((t) => { t.enabled = false; t.stop(); }); } catch (_) {}
      }
      setMicSpeaking(false);
    };
  }, [micOn, effectiveSection, getAudioConstraints]);

  // ── Effect 2: Web Speech API Fallback ────────────────────────────────────
  // Owner spec 2026-05-03 (PTT): drop the `hasSpokenDesktopIntro` gate.
  // User pressing `\` opens the mic and we capture immediately — no
  // waiting on the intro greeting to finish.
  useEffect(() => {
    if (!micOn || effectiveSection !== 'ask' || introLocked || introLockedRef.current) {
      setVoiceError('');
      setVoiceListening(false);
      return;
    }
    if (isDesktopApp) { setVoiceError(''); return; }
    if (vadCanRun) { setVoiceListening(false); return; }
    if (!SpeechRecognitionAPI) {
      setVoiceError('Voice requires Chrome/Edge, or a backend with OPENAI_API_KEY for server transcription.');
      setVoiceListening(false);
      return;
    }
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setVoiceError(micInsecureContextMessage);
      setVoiceListening(false);
      return () => {};
    }
    setVoiceError('');
    let lastSent = 0;
    let lastTranscript = '';
    let listeningStartedAt = 0;
    let restartCount = 0;
    const MAX_AUTO_RESTARTS = 6;
    const MIN_MS_BETWEEN_VOICE = 1200;
    // Identical utterance inside this window = a re-delivery, not the user
    // repeating themselves. Long enough to kill recognizer echo, short enough
    // that a real "yes" ... "yes" a few seconds apart still gets through.
    const SAME_TEXT_BLOCK_MS = 4000;
    const MIN_TRANSCRIPT_LEN = 2;
    const MIN_LISTENING_MS_BEFORE_NO_SPEECH = 3500;
    let cancelled = false;

    const trySend = (text) => {
      if (!text || text.trim().length < MIN_TRANSCRIPT_LEN) return;
      const clean = text.trim();
      const now = Date.now();
      const prev = lastVoiceSendRef.current;
      // Rate gate + identical-text gate, both off a shared ref so a restarted
      // recognizer (or a second instance from an effect re-run) can't bypass
      // them. The Web Speech API re-delivers a final result after a restart;
      // without the text gate that re-delivery became a second question.
      if (now - prev.at < MIN_MS_BETWEEN_VOICE) return;
      if (clean === prev.text && now - prev.at < SAME_TEXT_BLOCK_MS) return;
      lastVoiceSendRef.current = { text: clean, at: now };
      lastSent = now;
      setVoiceError('');
      setLiveTranscript('');
      unlockAudioForReply();
      const send = sendMessageWithTextRef.current;
      if (send) { send(text.trim()); }
      else { setInput(text.trim()); }
    };

    const startRec = () => {
      if (cancelled) return;
      try {
        const rec = new SpeechRecognitionAPI();
        recognitionRef.current = rec;
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = 'en-US';
        rec.onstart = () => { if (!cancelled) { setVoiceListening(true); listeningStartedAt = Date.now(); } };
        rec.onend = () => {
          if (cancelled) return;
          setVoiceListening(false);
          setLiveTranscript('');
          const listenedMs = listeningStartedAt ? Date.now() - listeningStartedAt : 0;
          if (lastTranscript.trim().length >= MIN_TRANSCRIPT_LEN) {
            trySend(lastTranscript);
            lastTranscript = '';
          }
          if (restartCount < MAX_AUTO_RESTARTS) {
            restartCount++;
            setTimeout(startRec, 300);
          } else {
            if (listenedMs >= MIN_LISTENING_MS_BEFORE_NO_SPEECH) setVoiceError('Still listening. Speak naturally, or type your message.');
          }
        };
        rec.onresult = (e) => {
          try {
            let full = '';
            for (let i = 0; i < e.results.length; i++) {
              const r = e.results[i];
              if (r && r[0]?.transcript) { const t = r[0].transcript.trim(); if (t) full += (full ? ' ' : '') + t; }
            }
            if (full.trim()) { lastTranscript = full.trim(); if (!cancelled) { setLiveTranscript(full.trim()); showLiveInBubble(full.trim()); } restartCount = 0; }
          } catch (_) {}
        };
        rec.onerror = (e) => {
          if (cancelled) return;
          setVoiceListening(false);
          if (e.error === 'not-allowed') { setVoiceError(microphoneDeniedMessage); return; }
          if (e.error === 'no-speech' && restartCount < MAX_AUTO_RESTARTS) { restartCount++; setTimeout(startRec, 300); return; }
          if (e.error === 'no-speech') { setVoiceError('Still listening - try speaking a little closer to your mic.'); return; }
          setVoiceError(e.error || 'Voice error.');
        };
        rec.start();
      } catch (err) {
        if (!cancelled) { setVoiceError('Voice not available.'); setVoiceListening(false); }
        recognitionRef.current = null;
      }
    };

    (async () => {
      if (navigator.mediaDevices?.getUserMedia) {
        try { const s = await navigator.mediaDevices.getUserMedia({ audio: true }); s.getTracks().forEach((t) => t.stop()); }
        catch (_) { if (!cancelled) setVoiceError(microphoneDeniedMessage); return; }
      }
      if (!cancelled) startRec();
    })();

    return () => {
      cancelled = true;
      setVoiceListening(false);
      setLiveTranscript('');
      try { if (recognitionRef.current) recognitionRef.current.stop(); } catch (_) {}
      recognitionRef.current = null;
    };
  }, [micOn, effectiveSection, isDesktopApp, vadCanRun, micInsecureContextMessage, microphoneDeniedMessage, introLocked, hasSpokenDesktopIntro]);

  // ── Effect 3: Main VAD ───────────────────────────────────────────────────
  // Owner spec 2026-05-03 (PTT): drop intro-gate AND voicePolicy gate.
  // User pressed `\` → start capturing. Period. introLocked also dropped
  // because intro may never run (TTS error etc.) and the user shouldn't
  // be blocked from talking while we wait for an intro greeting.
  useEffect(() => {
    if (effectiveSection !== 'ask' || !micOn || voiceEnrolling) {
      console.log('[VAD] bootstrap blocked: section=', effectiveSection, 'micOn=', micOn, 'voiceEnrolling=', voiceEnrolling);
      setVoiceRecording(false);
      return undefined;
    }
    console.log('[VAD] bootstrap proceeding — micOn=true, getting user media...');
    // VOICE-POLICY GATE DISABLED — PTT means user explicitly opened mic;
    // we don't need a separate "free" / "name_only" / "hold" policy state
    // controlling whether to record. Mic on = record.
    if (!vadCanRun && !isDesktopApp) {
      setVoiceRecording(false);
      return undefined;
    }
    if (!isDesktopApp && typeof window !== 'undefined' && window.isSecureContext === false) {
      setVoiceRecording(false);
      setVoiceError(micInsecureContextMessage);
      return undefined;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceError('Microphone not supported in this browser.');
      return undefined;
    }
    const recorderMime = pickMediaRecorderMime();
    if (recorderMime === null) {
      setVoiceError('Audio recording not supported in this browser. Try Chrome, Edge, Firefox, or Safari.');
      return undefined;
    }

    let cancelled = false;
    let stream = null;
    let audioCtx = null;
    let analyser = null;
    let rafId = null;
    let mediaRecorder = null;
    let recordChunks = [];
    let recordMime = 'audio/webm';
    let vadPhase = 'idle';
    let smoothed = 0;
    // Dynamic noise floor for transcription trigger — adapts to the room over time.
    let vadNoiseFloor = VAD_START_RMS * 0.5;
    let speechCandidateAt = null;
    let utteranceStartMs = 0;
    let silenceStartMs = null;
    let cooldownUntil = 0;
    let freqBuf = null;
    let freqSum = null;
    let freqFrameCount = 0;
    let enhancedAccum = null; // Layer 1: enhanced 20-d embedding accumulator
    // PTT release (owner spec 2026-06-25): turning the mic OFF mid-utterance must
    // FINALIZE + send what was said (so Aria replies), NOT discard it. This flag
    // tells finalizeRecording's onstop to bypass the "muted → drop" gate.
    let muteFinalize = false;

    const stopAll = () => {
      if (rafId != null) {
        clearTimeout(rafId);
        rafId = null;
      }
      // Disable tracks BEFORE stopping the recorder, so any audio the MediaRecorder
      // is currently flushing has no hardware-level audio to emit.
      if (stream) {
        try { stream.getTracks().forEach((t) => { t.enabled = false; }); } catch (_) {}
      }
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        try { mediaRecorder.stop(); } catch (_) {}
      }
      mediaRecorder = null;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        stream = null;
      }
      if (audioCtx) {
        try { audioCtx.close(); } catch (_) {}
        audioCtx = null;
      }
    };

    const handleTranscribeError = (err) => {
      const msg = err && err.message ? String(err.message) : '';
      const lower = msg.toLowerCase();
      if (msg.includes('USER_ID_REQUIRED')) {
        setVoiceError(isDev ? 'Voice billing: sign in with a plan, or for local dev set ALLOW_ANONYMOUS_AI=1 in HowTo-Backend/.env and restart the backend.' : 'Voice requires an active plan. Check your subscription.');
      } else if (msg.includes('OUT_OF_CREDITS')) {
        setVoiceError('AI usage limit reached for this month. Check Subscription or add credits.');
      } else if (msg.includes('TRANSCRIBE_NOT_AVAILABLE') || msg.includes('OPENAI_API_KEY')) {
        setVoiceError(isDev ? 'Voice transcription requires OPENAI_API_KEY in HowTo-Backend/.env. Add the key and restart the backend.' : 'Voice isn\'t available right now.');
      } else if (lower.includes('audio_too_short') || lower.includes('too short')) {
        setVoiceError('Too short — speak a full sentence, then pause.');
      } else if (
        lower.includes('failed to fetch') ||
        lower.includes('load failed') ||
        lower.includes('networkerror') ||
        lower.includes('timeout') ||
        lower.includes('abort') ||
        lower.includes('aborted')
      ) {
        // NOT HIS INTERNET (Braeden 9/6): this posts to 127.0.0.1:3106 or
        // 127.0.0.1:3001 - his own machine - so a failed transcribe is her
        // ear or her backend being down, never his connection.
        setVoiceError(isDev ? 'Cannot reach the backend for transcription. Start HowTo-Backend (npm start), then open http://127.0.0.1:3001/health — if that works, quit and reopen Aria.' : cannotReachMessage('Aria'));
      } else if (lower.includes('413') || lower.includes('payload too large')) {
        setVoiceError('Audio clip too large. Speak shorter sentences.');
      } else if (lower.includes('401') || lower.includes('unauthorized')) {
        setVoiceError(msg.includes('USER_ID') ? 'Voice requires userId or ALLOW_ANONYMOUS_AI=1 (local dev). See console.' : 'Backend API key rejected. Check OPENAI_API_KEY in .env.');
      } else if (lower.includes('429') || lower.includes('rate limit')) {
        setVoiceError('Rate limited by transcription API. Wait a moment and try again.');
      } else if (msg) {
        setVoiceError(msg);
      } else {
        setVoiceError('Transcription failed. Check backend logs for details.');
      }
    };

    let earlyVoiceGateChecked = false;
    let earlyVoiceGatePassed = false;

    const checkVoiceGateEarly = () => {
      if (earlyVoiceGateChecked) return earlyVoiceGatePassed;
      const gate = voiceProfileGateRef.current;
      if (!gate.embedding || !gate.strict) { earlyVoiceGateChecked = true; earlyVoiceGatePassed = true; return true; }
      if (!freqSum || freqFrameCount < VOICE_GATE_EARLY_FRAMES) return true;

      // Use enhanced 20-d embedding if available, fall back to raw FFT sum
      let sim;
      if (enhancedAccum && enhancedAccum.frameCount >= VOICE_GATE_EARLY_FRAMES) {
        const enhancedVec = enhancedAccum.getEmbedding();
        if (enhancedVec && gate.embedding.length === enhancedVec.length) {
          sim = cosineSimilarity(gate.embedding, enhancedVec);
        }
      }
      if (sim === undefined) {
        const vec = sumToMeanVector(freqSum, freqFrameCount);
        if (!vec) return true;
        sim = cosineSimilarity(gate.embedding, vec);
      }

      if (sim < gate.threshold) {
        console.log('[VAD] voice gate REJECTED: similarity', sim.toFixed(3), '< threshold', gate.threshold, '-- not the enrolled user');
        earlyVoiceGateChecked = true;
        earlyVoiceGatePassed = false;
        if (mediaRecorder && mediaRecorder.state !== 'inactive') { try { mediaRecorder.stop(); } catch (_) {} }
        recordChunks = [];
        vadPhase = 'idle';
        cooldownUntil = Date.now() + VAD_COOLDOWN_MS;
        if (!cancelled) setVoiceRecording(false);
        return false;
      }
      if (freqFrameCount >= MIN_FRAMES_FOR_MATCH) { earlyVoiceGateChecked = true; earlyVoiceGatePassed = true; }
      return true;
    };

    const finalizeRecording = () => {
      if (vadPhase !== 'recording' || !mediaRecorder || mediaRecorder.state === 'inactive') return;
      const mr = mediaRecorder;
      vadPhase = 'transcribing';
      if (!cancelled) setVoiceRecording(false);
      mr.onstop = () => {
        const blob = new Blob(recordChunks, { type: recordMime });
        recordChunks = [];
        mediaRecorder = null;
        // ── Mute gate ──────────────────────────────────────────────────────
        // If the user muted mid-utterance, normally we'd drop the blob. BUT if
        // this stop was triggered BY the mute itself (PTT release — owner opened
        // the mic, spoke, then turned it off to send), we KEEP it: finalize and
        // send so Aria replies. Only a non-mute-triggered mute drops.
        if (!micOnRef.current && !muteFinalize) {
          console.log('[VAD] onstop: mic is muted — dropping blob, not transcribing');
          vadPhase = 'idle';
          return;
        }
        if (muteFinalize) console.log('[VAD] onstop: PTT release — finalizing utterance despite mic off');
        // NOTE: do NOT check `cancelled` here — the effect may tear down while transcription
        // is in-flight (e.g. other state change). We still want to deliver the result
        // when micOn is still true.
        if (blob.size < 500) {
          console.log('[VAD] blob too small:', blob.size);
          setVoiceError("Sorry, I didn't hear that. What was that?");
          vadPhase = 'idle';
          cooldownUntil = Date.now() + VAD_COOLDOWN_MS;
          return;
        }
        const gate = voiceProfileGateRef.current;
        // Build enhanced embedding for this utterance (prefer 20-d, fall back to raw FFT)
        let utterVecForAdapt = null;
        let enhancedEmbed = enhancedAccum ? enhancedAccum.getEmbedding() : null;
        if (enhancedEmbed && gate.embedding && gate.embedding.length === enhancedEmbed.length) {
          utterVecForAdapt = enhancedEmbed;
        } else {
          utterVecForAdapt = (freqSum && freqFrameCount >= MIN_FRAMES_FOR_MATCH) ? sumToMeanVector(freqSum, freqFrameCount) : null;
        }
        // Whether THIS utterance positively matched the enrolled voiceprint.
        // Consumed by the phantom-filler gate: a verified-owner "hey" always
        // goes through; an unverified one needs conversational context.
        let voiceGateStrictPassed = false;
        if (gate.embedding && gate.strict && utterVecForAdapt) {
          const sim = cosineSimilarity(gate.embedding, utterVecForAdapt);
          if (sim < gate.threshold) {
            console.log('[VAD] post-transcription voice gate REJECTED: sim', sim.toFixed(3), '< threshold', gate.threshold);
            vadPhase = 'idle'; cooldownUntil = Date.now() + VAD_COOLDOWN_MS; return;
          }
          // Record match score for adaptive threshold learning
          recordMatchScore(sim);
          voiceGateStrictPassed = true;
        }
        // ── Attention gate ──
        // Owner spec 2026-05-03: PTT model. Mic is OFF by default; the
        // user explicitly opens it by pressing `\`. That gesture IS the
        // "I'm talking to you" signal — no wake word needed. We bypass
        // the entire wake-word classifier when the mic is open and just
        // send everything captured straight to conversation.
        //
        // The wake-word classifier still exists for cases where someone
        // builds an always-on variant — but in the current build, mic
        // can only be on if user pressed `\`.
        const sleepingAtCapture = ariaAttentionRef.current === 'sleeping';
        console.log('[VAD] finalizeRecording — blob size:', blob.size, 'sleeping:', sleepingAtCapture, 'sending to transcribe...');
        transcribeDesktopBlobRef.current(blob, recordMime)
          .then((res) => {
            // Do NOT check `cancelled` — transcription must deliver even if effect tore down
            // transcribeDesktopBlob now returns { text, gate, speech } — the
            // server's ECAPA voiceprint verdict + whisper's own speech
            // confidence ride along with the transcript. Old string shape
            // still accepted (older App.jsx builds).
            const text = (res && typeof res === 'object') ? (res.text || '') : (res || '');
            const srvGate = (res && typeof res === 'object') ? res.gate : null;
            const srvSpeech = (res && typeof res === 'object') ? res.speech : null;
            // ── ACOUSTIC READ (2026-08-03) ────────────────────────────────
            // pitch / loudness / pace / emotion, computed server-side and
            // until now dropped on the floor by this very handler. The
            // WebSocket streaming path has always forwarded its read; this
            // MediaRecorder fallback (which takes over after 3 streaming
            // failures, and is the ONLY path on Electron and iOS) did not, so
            // Aria went acoustically deaf on those turns with nothing logged.
            const srvRead = (res && typeof res === 'object') ? res.read : null;
            console.log('[VAD] transcription result:', JSON.stringify(text), 'gate:', srvGate ? srvGate.reason : 'n/a', 'speech:', srvSpeech ? JSON.stringify(srvSpeech) : 'n/a', 'sleepingAtCapture:', sleepingAtCapture);
            setVoiceError('');
            if (!text) return;
            // Hand the read up so the NEXT turn can attach it as body.voiceRead,
            // exactly as streamingTranscribe's onFinal does. Stored only for
            // turns that will actually be sent, so a dropped utterance cannot
            // leave a stale read to bleed into the following one.
            // 8/23: carry whisper's own confidence up with the read. He spoke,
            // whisper produced "I mean subscribe to a lot of Zaid", and she
            // answered "Ok." - because nothing downstream knew the transcript
            // was doubtful. The numbers existed at the server and died here.
            if (srvRead || srvSpeech) {
              try { onVoiceRead?.({ ...(srvRead || {}), asr: srvSpeech || null }); } catch (_) {}
            }
            // ── SERVER VOICE-ID GATE — the authority (2026-07-15) ──────────
            // ariaVoiceProfile verified the ECAPA voiceprint server-side and
            // said "this is not the enrolled user" (other) or "not a human
            // voice" (noise). The server has ALWAYS sent this verdict; the
            // client used to read only .text and answer anyway.
            // ── VOICE ID GATES DECISIONS, NOT HEARING (Braeden 8/16) ──────
            //
            //   "its not that she doesnt need to not respond, she just doesnt
            //    make certian desicions without the right owner talking"
            //
            // This was the SECOND copy of the gate. I fixed the streaming one
            // in whisperProxyWs.js and never knew this existed — so on every
            // turn the MediaRecorder path handled, his words were still being
            // binned on a voiceprint verdict. It logged only to the browser
            // console, which is why the server saw neither voice.sent NOR
            // voice.dropped: the text died before it reached either.
            //
            // The print behind it had been rejecting him four times out of
            // five, and a ~11.5% EER verifier cannot be the thing that decides
            // whether she can hear. So the turn goes THROUGH, tagged, and the
            // layers that move money refuse on the tag instead.
            const voiceUnverified = !!(srvGate && srvGate.allow === false);
            if (voiceUnverified) {
              console.log('[PIPELINE] voice unverified (' + srvGate.reason + ') — passing through tagged, NOT dropping');
              try {
                fetch('/api/pipeline/event', { method: 'POST', headers: { 'content-type': 'application/json' },
                  body: JSON.stringify({ stage: 'voice.unverified', level: 'warn',
                    text: String(text).slice(0, 60), meta: { reason: srvGate.reason, path: 'mediarecorder' } }) }).catch(() => {});
              } catch (_) {}
            }
            // ── Whisper's own "was this speech?" confidence ────────────────
            // segments===0 → Silero VAD found no speech at all in the blob.
            // High no_speech_prob on a short utterance → whisper itself
            // doubts a human said it. Long dictation is never touched.
            if (srvSpeech) {
              const wc = text.trim().split(/\s+/).filter(Boolean).length;
              if (srvSpeech.segments === 0 || (srvSpeech.no_speech_prob >= 0.75 && wc < 3)) {
                console.log('[PIPELINE] DROPPED by speech confidence: no_speech_prob', srvSpeech.no_speech_prob, 'segments', srvSpeech.segments, 'words', wc);
                // Visible on the server too. This one is a legitimate drop —
                // whisper itself doubts a human spoke — but a silent legitimate
                // drop is indistinguishable from a bug, and I have now spent
                // several rounds chasing exactly that.
                try {
                  fetch('/api/pipeline/event', { method: 'POST', headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ stage: 'voice.dropped', level: 'warn',
                      text: String(text).slice(0, 60),
                      meta: { why: 'speech_confidence', no_speech_prob: srvSpeech.no_speech_prob,
                              segments: srvSpeech.segments, words: wc } }) }).catch(() => {});
                } catch (_) {}
                vadPhase = 'idle';
                return;
              }
            }
            const serverVerifiedSelf = !!(srvGate && srvGate.reason === 'self');
            // PTT mode: user opened the mic intentionally → skip wake-word
            // classification entirely. Strip any leading "Aria" they happened
            // to say (so "aria what time is it" → "what time is it") then send.
            const stripped = stripWakeWord(text) || text;
            if (stripped.length < 2) {
              setVoiceError("Sorry, I didn't hear that. What was that?");
              return;
            }
            // ── Phantom-filler gate (2026-07-15) ─────────────────────────────
            // Whisper's classic hallucinations on ambient audio / music are
            // exactly the bare fillers: "Hey." "Thank you." "Thanks for
            // watching." A phantom "hey" got an instant fast-reply + TTS —
            // Aria "talking on her own". A bare filler only sends with
            // EVIDENCE the user actually spoke:
            //   • this utterance matched the enrolled voiceprint, or
            //   • Aria was already in conversation at capture time, or
            //   • the mic was opened <15s ago (fresh PTT press = intent).
            // Anything with ≥2 real words or the wake word is untouched.
            const FILLER_RE = /^(hey|hi|hello|yo|okay|ok|yeah|yep|you|so|oh|hmm|huh|uh|um|bye|goodbye|thanks|thank you|thanks for watching|thank you for watching|please subscribe|subscribe)[\s.!?,]*$/i;
            const approxAudioSec = blob.size / 4000; // webm-opus ≈ 4 KB/s
            if (FILLER_RE.test(stripped.trim())) {
              const micFresh = Date.now() - (micOpenedAtRef.current || 0) < 15000;
              // serverVerifiedSelf = ECAPA voiceprint match (strongest evidence);
              // voiceGateStrictPassed = local FFT profile (weaker fallback).
              const evidence = serverVerifiedSelf || voiceGateStrictPassed || !sleepingAtCapture || micFresh;
              // One filler word out of 3.5s+ of audio = whisper compressing
              // music into a greeting; no human "hey" takes that long. Drop
              // even mid-conversation.
              if (approxAudioSec > 3.5 || !evidence) {
                console.log('[PIPELINE] DROPPED phantom filler:', JSON.stringify(stripped),
                  'audio≈' + approxAudioSec.toFixed(1) + 's', 'voiceMatch:', voiceGateStrictPassed,
                  'sleepingAtCapture:', sleepingAtCapture, 'micFresh:', micFresh);
                vadPhase = 'idle';
                return;
              }
            }
            // Same-utterance dedup — shared ref with the Web Speech path, so a
            // re-delivered/echoed transcript can't become a second question.
            {
              const now = Date.now();
              const prev = lastVoiceSendRef.current;
              if (stripped === prev.text && now - prev.at < 4000) {
                console.log('[PIPELINE] DROPPED duplicate desktop transcript:', stripped.slice(0, 40));
                return;
              }
              lastVoiceSendRef.current = { text: stripped, at: now };
            }
            // Only a SENT utterance wakes her. Setting attentive before the
            // gates let one phantom whitelist the next (sleeping→attentive).
            try { ariaAttentionRef.current = 'attentive'; } catch (_) {}
            // ── FULL-SENTENCE DOCTRINE (Braeden 8/12): stitch a held fragment
            // onto this chunk, and if the RESULT still reads unfinished, hold
            // it and keep listening — she does not answer a half sentence.
            // muteFinalize (explicit mic close) always sends: that IS "done".
            {
              const _pend = pendingUtteranceRef.current;
              const _nowMs = Date.now();
              const joined = (_pend.text && _nowMs - _pend.at < 12000)
                ? (_pend.text + ' ' + stripped).trim()
                : stripped;
              if (pendingSendTimerRef.current) { clearTimeout(pendingSendTimerRef.current); pendingSendTimerRef.current = null; }
              if (!muteFinalize && readsUnfinished(joined)) {
                pendingUtteranceRef.current = { text: joined, at: _nowMs };
                try { setLiveTranscript(joined + ' …'); } catch (_) {}
                console.log('[PIPELINE] ⏸ HOLDING — sentence reads unfinished, still listening:', JSON.stringify(joined.slice(-50)));
                // If nothing more comes in 5s they really did stop — send it.
                pendingSendTimerRef.current = setTimeout(() => {
                  const p = pendingUtteranceRef.current;
                  pendingUtteranceRef.current = { text: '', at: 0 };
                  pendingSendTimerRef.current = null;
                  if (p.text) {
                    console.log('[PIPELINE] ⏱ hold expired — sending as-is:', JSON.stringify(p.text.slice(0, 60)));
                    queueOrSendDesktopTranscriptRef.current(p.text);
                  }
                }, 5000);
                vadPhase = 'idle';
                return;
              }
              pendingUtteranceRef.current = { text: '', at: 0 };
              console.log('[PIPELINE] 🟢 PTT — sending FULL utterance to conversation:', joined.slice(0, 60));
              queueOrSendDesktopTranscriptRef.current(joined);
              return;
            }
            // ─── (legacy wake-word gate kept below for reference, never reached) ───
            // eslint-disable-next-line no-unreachable
            if (text) {
              const gate = voiceProfileGateRef.current || {};
              const hasVoiceProfile = !!gate.embedding;
              const requireWake = REQUIRE_WAKE_WORD_UNTIL_VOICE_ENROLLED && !hasVoiceProfile;
              console.log('[PIPELINE] requireWake:', requireWake, 'hasVoiceProfile:', hasVoiceProfile);
              if (requireWake) {
                const wakeResult = classifyWakeWord(text, ariaAttentionRef.current);
                if (!wakeResult.isDirected) {
                  if (wakeResult.reason !== 'no_wake_word') {
                    console.log('[PIPELINE] Wake word NOT directed:', wakeResult.reason);
                  } else {
                    setVoiceError('Say "Aria" to get my attention.');
                  }
                  return;
                }
                console.log('[PIPELINE] Wake word directed:', wakeResult.reason, 'conf:', wakeResult.confidence.toFixed(2));
                const stripped = stripWakeWord(text);
                if (!stripped) {
                  setVoiceError("I'm listening. What can I help you with?");
                  return;
                }
                queueOrSendDesktopTranscriptRef.current(stripped);
                return;
              }
              if (awaitingIntroNameRef.current) {
                console.log('[VAD] awaitingIntroName — skipping name gate, clearing flag');
                awaitingIntroNameRef.current = false;
                // Fall through to normal send path instead of blocking
              }
              if (false && awaitingIntroNameRef.current) {
                const candidate = cleanNameFromRaw(text);
                if (!isLikelyUserName(candidate)) {
                  setVoiceError("I'm listening for your name. Please just say your name.");
                  return;
                }
                awaitingIntroNameRef.current = false;
                try {
                  localStorage.setItem(ARIA_PREFERRED_NAME_KEY, candidate);
                  localStorage.setItem(ARIA_PREFERRED_NAME_BACKUP_KEY, candidate);
                  preferredNameRef.current = candidate;
                } catch (_) {}
                setVoicePolicy('hold');
                setMicOn(false);
                queueOrSendDesktopTranscriptRef.current(`My name is ${candidate}.`);
                return;
              }
              if (utterVecForAdapt) {
                const existingProfile = loadVoiceProfile();
                if (existingProfile && existingProfile.embedding) {
                  const count = existingProfile.adaptationCount || 1;
                  const blended = blendEMA(existingProfile.embedding, utterVecForAdapt, momentumForAdaptationCount(count));
                  saveVoiceProfile(blended, true, { enrolledAt: existingProfile.enrolledAt, adaptationCount: count + 1 });
                  syncVoiceProfileGate();
                  setVoiceAdaptationCount(count + 1);
                } else if (!existingProfile) {
                  const now = new Date().toISOString();
                  saveVoiceProfile(utterVecForAdapt, true, { enrolledAt: now, adaptationCount: 1 });
                  syncVoiceProfileGate();
                  setVoiceProfileEnrolled(true);
                  setVoiceMatchStrict(true);
                  setVoiceAdaptationCount(1);
                  try { localStorage.setItem(VOICE_ONBOARDING_DONE_KEY, '1'); } catch (_) {}
                  console.log('[Aria voice] Auto-enrolled voice profile from first utterance');
                }
              }

              // ── Cost gate: drop transcripts that are clearly not intentional queries ──────
              // Whisper sometimes returns fragments from ambient sound / background speech.
              // If the transcript has < 3 real words AND no question/command pattern,
              // discard silently rather than burning an LLM API call.
              //
              // EXCEPTIONS — these always pass through, even if short:
              //   • The wake word "Aria" — user clearly addressing her
              //   • Common acks/greetings ("hi", "hey", "yeah", "thanks",
              //     "ok", "no") — handled instantly by the fast-reply layer
              //     anyway, so passing them is essentially free
              //   • When Aria is already attentive (mid-conversation),
              //     short follow-ups are valid
              //
              // Owner spec 2026-05-02: Aria must respond when the user
              // talks. The previous gate was silently eating short
              // utterances which is exactly what users say first. Common
              // short phrases ARE the conversation starters — gate them
              // and Aria appears broken.
              const queryWords = /\b(what|how|why|who|when|where|can|could|would|should|is|are|do|does|will|tell|explain|help|show|find|create|make|set|open|write|search|give|list|describe|define|translate|calculate|convert|remind|schedule|play|stop|pause|skip|next|summarize|compare|recommend|suggest|check|read|send|call|start|run|build|fix|change|add|delete|remove|update|get|go)\b/i;
              const realWords = text.trim().split(/\s+/).filter((w) => w.length > 1);
              const trimmedLower = text.trim().toLowerCase().replace(/[.!?,]/g, '');
              const hasWakeWord = /\b(aria|arya|ariel)\b/i.test(text);
              const isCommonAck = /^(hi|hey|hello|yo|sup|howdy|yeah|yes|yep|sure|ok|okay|got it|nope|no|nah|thanks|thank you|thx|cool|nice|alright|perfect|right|correct|exactly|bye|goodbye|later|wait|hmm|huh|um)$/i.test(trimmedLower);
              const isAttentive = (typeof ariaAttentionRef !== 'undefined' && ariaAttentionRef?.current === 'attentive');
              const looksLikeQuery = realWords.length >= 3 || text.includes('?') || queryWords.test(text);
              // ── SHE ASKED. THE ANSWER IS NOT NOISE. (Braeden 8/16) ──────
              //
              //   "still not working right, didnt pick up when i was tryign to
              //    talk back to it"
              //
              // This gate exists to avoid paying for room noise when nobody is
              // talking to her, and it was binning the entire onboarding road.
              // Measured against the answers her own script asks for:
              //   "not really" DROPPED   "a little" DROPPED   "a bit" DROPPED
              //   "roofing"    DROPPED   "plumbing" DROPPED   "every day" DROPPED
              // She asks "are you familiar with AI?", he says "not really", and
              // the client throws it away before it ever leaves the browser.
              //
              // An answer to a question she just asked is the opposite of
              // accidental noise, so while she is waiting on one, nothing here
              // is gated for cost.
              const sheIsWaiting = !!(expectingAnswerRef && expectingAnswerRef.current);
              const shouldPass = sheIsWaiting || looksLikeQuery || hasWakeWord || isCommonAck || isAttentive;
              if (!shouldPass) {
                console.log('[PIPELINE] DROPPED by cost gate — too short/no intent:', text, 'words:', realWords.length);
                try {
                  fetch('/api/pipeline/event', { method: 'POST', headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ stage: 'voice.dropped', level: 'warn', text: String(text).slice(0, 60),
                      meta: { why: 'cost_gate', words: realWords.length } }) }).catch(() => {});
                } catch (_) {}
                vadPhase = 'idle';
                return;
              }
              if (!looksLikeQuery) {
                console.log('[PIPELINE] short utterance — passing because:',
                  hasWakeWord ? 'wake-word' : isCommonAck ? 'common-ack' : 'attentive');
              }

              // ── Layer 3: Intent gate — check if utterance is directed at Aria ──
              // Bypassed when:
              //   • The user said "Aria" (wake word — explicit address)
              //   • Aria is already attentive (mid-conversation follow-up)
              //   • It's a common ack/greeting (passed cost gate above)
              // Without these bypasses the intent classifier was dropping
              // voice messages users clearly meant for Aria.
              const intent = lastTranscribeIntentRef?.current;
              // sheIsWaiting for the same reason as the cost gate above: an
              // answer to her own question cannot be "not directed at Aria".
              // Without it "not really" cleared the cost gate and was then
              // binned here instead, which is the same silence one layer down.
              const intentBypass = sheIsWaiting || hasWakeWord || isCommonAck || isAttentive;
              if (!intentBypass && intent && intent.category !== 'interrupt') {
                const ctx = conversationContextRef.current || {};
                const boost = computeContextBoost(ctx, text);
                const adjustedConfidence = (intent.confidence || 0) + boost;
                if (!intent.isForAria && adjustedConfidence < 0.5) {
                  console.log('[PIPELINE] DROPPED by intent gate:', intent.category, 'conf:', intent.confidence?.toFixed(2), 'boost:', boost.toFixed(2), 'text:', text.substring(0, 50));
                  try {
                    fetch('/api/pipeline/event', { method: 'POST', headers: { 'content-type': 'application/json' },
                      body: JSON.stringify({ stage: 'voice.dropped', level: 'warn', text: String(text).slice(0, 60),
                        meta: { why: 'intent_gate', category: intent.category, conf: intent.confidence } }) }).catch(() => {});
                  } catch (_) {}
                  vadPhase = 'idle';
                  lastTranscribeIntentRef.current = null;
                  return;
                }
                if (intent.isForAria) {
                  console.log('[PIPELINE] Intent gate PASS:', intent.category, 'conf:', adjustedConfidence.toFixed(2));
                }
              } else if (intentBypass) {
                console.log('[PIPELINE] Intent gate BYPASSED:',
                  hasWakeWord ? 'wake-word' : isCommonAck ? 'common-ack' : 'attentive');
              }
              lastTranscribeIntentRef.current = null; // consume the intent result

              console.log('[PIPELINE] passed all gates, sending to AI:', text);

              // ── Interrupt-or-queue: if Aria is speaking, only stop her on explicit interrupt ──
              if (ariaSpeakingRef.current) {
                if (isInterruptCommand(text)) {
                  // User explicitly told Aria to stop — cut her off now
                  console.log('[PIPELINE] ✋ Interrupt command while Aria speaking:', text);
                  stopAriaPlaybackRef.current();
                  vadPhase = 'idle';
                  return;
                }
                // Not an interrupt — queue the message for after Aria finishes speaking.
                // Aria should complete her thought, then we process the user's input.
                console.log('[PIPELINE] 🗣️ Aria speaking, queuing user message:', text.substring(0, 50));
                const waitForAriaToFinish = () => {
                  if (ariaSpeakingRef.current) {
                    setTimeout(waitForAriaToFinish, 200);
                    return;
                  }
                  console.log('[PIPELINE] Aria finished speaking, now sending queued message:', text.substring(0, 50));
                  queueOrSendDesktopTranscriptRef.current(text);
                };
                waitForAriaToFinish();
                vadPhase = 'idle';
                return;
              }

              // ── Break interval learning: update local preference if user states it ─────
              const breakPrefMatch = text.match(/\b(?:remind me|break)\s+every\s+(\d+)\s*(min(?:utes?)?|hours?)\b/i)
                || text.match(/\btake a break every\s+(\d+)\s*(min(?:utes?)?|hours?)\b/i);
              if (breakPrefMatch) {
                const n = parseInt(breakPrefMatch[1], 10);
                const unit = breakPrefMatch[2].toLowerCase().startsWith('h') ? 60 : 1;
                const newMins = Math.max(15, Math.min(n * unit, 120));
                try { localStorage.setItem('aria_break_interval_mins', String(newMins)); } catch (_) {}
                breakIntervalMsRef.current = newMins * 60 * 1000;
              }

              // ── Stop/pause detection: user telling Aria to go quiet ────────────────────
              // If user says stop/quiet/shut off, pause proactive check-ins until they speak again.
              const stopWords = /\b(stop|quiet|shut (?:up|off|down)|that'?s? (?:all|enough|ok|okay)|never mind|i'?m good|go away|leave me alone|pause)\b/i;
              if (stopWords.test(text) && realWords.length <= 5) {
                // Short stop command — acknowledge but don't send to LLM
                console.log('[PIPELINE] DROPPED by stop-word gate:', text);
                proactivePausedRef.current = true;
                vadPhase = 'idle';
                return;
              }
              // Any real user message resumes proactive mode
              proactivePausedRef.current = false;
              // ─────────────────────────────────────────────────────────────────────────────

              // ── Attention: user spoke while attentive — reset idle timer ──
              resetAttentionTimer();

              // Check if user said "Aria" mid-conversation — use Layer 2 classifier
              const lowerText = text.toLowerCase();
              const wakeCheck = classifyWakeWord(text, 'attentive');
              // Once attentive, conversation flows naturally — no per-turn name gate.
              // Wake word is enforced only in sleeping mode (via Web Speech listener).
              if (wakeCheck.isDirected || wakeCheck.reason === 'no_wake_word') {
                // Either directed wake word or no wake word at all — send normally
                const cleaned = wakeCheck.reason !== 'no_wake_word' ? stripWakeWord(text) : text;
                if (cleaned && cleaned.length > 2) {
                  queueOrSendDesktopTranscriptRef.current(cleaned);
                } else if (wakeCheck.reason === 'solo_callout') {
                  // User just said "Aria" — mark pending wake for next utterance
                  if (conversationContextRef?.current) {
                    conversationContextRef.current.pendingWakeWordAt = Date.now();
                  }
                  queueOrSendDesktopTranscriptRef.current('aria');
                } else {
                  queueOrSendDesktopTranscriptRef.current(text);
                }
                // Handle delegation flag
                if (wakeCheck.isDelegation && conversationContextRef?.current) {
                  conversationContextRef.current.pendingDelegation = true;
                  conversationContextRef.current.pendingDelegationAt = Date.now();
                }
              } else {
                // Wake word present but NOT directed ("I told Aria about it") — still send
                // since we're attentive and the intent gate already passed
                queueOrSendDesktopTranscriptRef.current(text);
              }
            }
          })
          .catch((err) => { handleTranscribeError(err); })
          .finally(() => { vadPhase = 'idle'; cooldownUntil = Date.now() + VAD_COOLDOWN_MS; });
      };
      try {
        mr.stop();
      } catch (_) {
        vadPhase = 'idle';
        if (!cancelled) setVoiceRecording(false);
      }
    };

    const abortRecordingDiscard = () => {
      if (vadPhase !== 'recording' || !mediaRecorder) return;
      const mr = mediaRecorder;
      vadPhase = 'idle';
      speechCandidateAt = null;
      freqBuf = null;
      freqSum = null;
      freqFrameCount = 0;
      enhancedAccum = null;
      earlyVoiceGateChecked = false;
      earlyVoiceGatePassed = false;
      mr.onstop = () => {
        recordChunks = [];
        mediaRecorder = null;
        if (!cancelled) setVoiceRecording(false);
        cooldownUntil = Date.now() + VAD_COOLDOWN_MS * 2;
      };
      try {
        mr.stop();
      } catch (_) {
        mediaRecorder = null;
        vadPhase = 'idle';
        if (!cancelled) setVoiceRecording(false);
        cooldownUntil = Date.now() + VAD_COOLDOWN_MS * 2;
      }
    };

    const tick = () => {
      if (cancelled) return;
      // Hard mute gate: if the user toggled mic off, stop capturing. If an
      // utterance is IN PROGRESS, FINALIZE it (PTT release → transcribe + send
      // so Aria still replies) instead of dropping it. Otherwise just go idle.
      if (!micOnRef.current) {
        if (vadPhase === 'recording' && mediaRecorder && mediaRecorder.state !== 'inactive') {
          muteFinalize = true;
          finalizeRecording();  // sets vadPhase='transcribing', transcribes + sends
          return;
        }
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          try { mediaRecorder.stop(); } catch (_) {}
        }
        vadPhase = 'idle';
        return;
      }
      try {
      if (audioCtx && audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
      }
      if (!analyser) return;
      const raw = computeMicRms(analyser);
      // Asymmetric smoothing: rise fast (hear speech immediately), fall slow (don't clip mid-sentence).
      smoothed = raw > smoothed
        ? 0.4 * raw + 0.6 * smoothed   // fast attack
        : 0.05 * raw + 0.95 * smoothed; // slow release — hold energy between words so we don't clip mid-sentence

      const now = Date.now();

      // Periodic diagnostic — log every 3s so we can see what VAD is doing
      if (!tick._diagAt || now - tick._diagAt > 3000) {
        tick._diagAt = now;
        const trackState = stream?.getAudioTracks()?.[0]?.readyState || 'unknown';
        const startThresholdDiag = ariaSpeakingRef.current ? VAD_DURING_TTS_RMS : Math.max(VAD_START_RMS, vadNoiseFloor * 1.8);
        console.log(`[VAD-DIAG] rms:${smoothed.toFixed(5)} raw:${raw.toFixed(5)} thresh:${startThresholdDiag.toFixed(5)} floor:${vadNoiseFloor.toFixed(5)} phase:${vadPhase} attn:${ariaAttentionRef.current} audioCtx:${audioCtx?.state} track:${trackState} cooldown:${now < cooldownUntil} ttsGate:${now < ttsPostPlaybackUntilRef.current}`);
      }

      // Dead mic detection: if RMS stays exactly 0 for 5 seconds, the mic hardware isn't
      // delivering audio (common with Bluetooth headphones in A2DP mode). Restart with system default.
      if (raw === 0) {
        if (!tick._zeroSince) tick._zeroSince = now;
        else if (now - tick._zeroSince > 5000 && !tick._deadMicRecoveryAttempted) {
          tick._deadMicRecoveryAttempted = true;
          console.log('[VAD] ⚠️ Dead mic detected (0 RMS for 5s) — attempting recovery');
          // Stop current dead stream and try to find a working mic
          stopAll();
          (async () => {
            try {
              // Enumerate all audio input devices and try non-Bluetooth ones first
              const devices = await navigator.mediaDevices.enumerateDevices();
              const audioInputs = devices.filter(d => d.kind === 'audioinput');
              console.log('[VAD] Available mics:', audioInputs.map(d => `${d.label} (${d.deviceId.substring(0, 8)})`).join(', '));
              // Prefer built-in mic over Bluetooth (Bluetooth is likely the dead one)
              const builtIn = audioInputs.find(d => /built-in|macbook|internal|default/i.test(d.label) && !/bluetooth/i.test(d.label));
              const fallback = audioInputs.find(d => !/bluetooth/i.test(d.label));
              const target = builtIn || fallback;
              const constraints = target
                ? { audio: { deviceId: { exact: target.deviceId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true } }
                : { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } };
              console.log('[VAD] 🔄 Trying recovery mic:', target?.label || 'system default');
              stream = await navigator.mediaDevices.getUserMedia(constraints);
              const newTrack = stream.getAudioTracks()[0];
              console.log('[VAD] ✅ Recovery mic active:', newTrack?.label || 'unknown');
              if (newTrack?.label) setActiveMicLabel(newTrack.label);
              audioCtx = new (window.AudioContext || window.webkitAudioContext)();
              const source = audioCtx.createMediaStreamSource(stream);
              analyser = audioCtx.createAnalyser();
              analyser.fftSize = 512;
              analyser.smoothingTimeConstant = 0.75;
              source.connect(analyser);
              tick._zeroSince = null;
              tick._diagAt = null;
              rafId = setTimeout(tick, 30);
            } catch (e) {
              console.error('[VAD] ❌ Recovery mic failed:', e.message);
            }
          })();
          return; // stop current tick chain, recovery will restart it
        }
      } else {
        tick._zeroSince = null;
        tick._deadMicRecoveryAttempted = false;
      }

      if (vadPhase === 'transcribing') {
        rafId = setTimeout(tick, 30);
        return;
      }

      if (now < cooldownUntil) {
        rafId = setTimeout(tick, 30);
        return;
      }

      if (vadPhase === 'idle') {
        if (now < ttsPostPlaybackUntilRef.current) {
          speechCandidateAt = null;
          rafId = setTimeout(tick, 30);
          return;
        }
        // Adapt noise floor during silence so VAD triggers relative to the environment, not a fixed RMS.
        if (smoothed < vadNoiseFloor * 1.8) {
          vadNoiseFloor = 0.997 * vadNoiseFloor + 0.003 * smoothed;
          vadNoiseFloor = Math.max(0.001, Math.min(vadNoiseFloor, VAD_START_RMS));
        }
        const dynamicStart = Math.max(VAD_START_RMS, vadNoiseFloor * 1.8);
        const startThreshold = ariaSpeakingRef.current ? VAD_DURING_TTS_RMS : dynamicStart;
        // Also check spectrum — if spectrum says speech (high confidence), allow even if RMS is borderline
        const spectrumEarly = analyzeSpeechSpectrum(analyser, audioCtx.sampleRate);
        const spectrumOverride = spectrumEarly.isSpeech && spectrumEarly.speechRatio >= 0.7 && smoothed >= 0.0012;
        if (smoothed >= startThreshold || spectrumOverride) {
          // Check if the sound is actually speech (not music, fan, TV, etc.)
          const spectrum = spectrumOverride ? spectrumEarly : analyzeSpeechSpectrum(analyser, audioCtx.sampleRate);
          if (!spectrum.isSpeech) {
            // Sound detected but it's not speech — ignore it
            speechCandidateAt = null;
            rafId = setTimeout(tick, 30);
            return;
          }
          // No barge-in — let Aria finish speaking. User's utterance will be
          // recorded alongside Aria's TTS and processed after transcription.
          // Only explicit interrupt commands ("stop", "shut up") will cut Aria off,
          // and that decision happens post-transcription in finalizeRecording.
          if (speechCandidateAt == null) speechCandidateAt = now;
          else if (now - speechCandidateAt >= VAD_SPEECH_START_MS) {
            const pickedMime = pickMediaRecorderMime();
            recordMime = pickedMime || 'audio/webm';
            try {
              mediaRecorder = pickedMime
                ? new MediaRecorder(stream, { mimeType: pickedMime })
                : new MediaRecorder(stream);
              if (mediaRecorder.mimeType) recordMime = mediaRecorder.mimeType;
            } catch (e) {
              if (!cancelled) setVoiceError('Recording not supported.');
              speechCandidateAt = null;
              rafId = setTimeout(tick, 30);
              return;
            }
            recordChunks = [];
            mediaRecorder.ondataavailable = (e) => { if (e.data && e.data.size) recordChunks.push(e.data); };
            mediaRecorder.start(250);
            vadPhase = 'recording';
            utteranceStartMs = now;
            silenceStartMs = null;
            speechCandidateAt = null;
            freqBuf = new Uint8Array(analyser.frequencyBinCount);
            freqSum = new Float32Array(analyser.frequencyBinCount);
            freqFrameCount = 0;
            enhancedAccum = new EnhancedEmbeddingAccumulator();
            console.log(`[VAD] ▶ recording started, rms:${smoothed.toFixed(4)} thresh:${startThreshold.toFixed(4)} speech:${spectrum.speechRatio.toFixed(2)} flat:${spectrum.spectralFlatness.toFixed(2)} centroid:${spectrum.centroid}Hz`);
            if (!cancelled) setVoiceRecording(true);
          }
        } else {
          speechCandidateAt = null;
        }
      } else if (vadPhase === 'recording') {
        // Don't abort recording while Aria speaks — let the user's utterance
        // be captured fully. Post-transcription logic will decide whether to
        // interrupt Aria (explicit stop command) or queue the message.
        if (freqBuf && freqSum) {
          analyser.getByteFrequencyData(freqBuf);
          addBinsToSum(freqSum, freqBuf);
          freqFrameCount++;
          // Layer 1: accumulate enhanced embedding frame
          if (enhancedAccum && analyser) {
            const frame = buildEnhancedFrame(analyser, audioCtx?.sampleRate || 48000);
            enhancedAccum.addFrame(frame);
          }
        }
        if (!checkVoiceGateEarly()) {
          // Log once per second when gate blocks
          if (!tick._gateLogAt || now - tick._gateLogAt > 2000) { tick._gateLogAt = now; console.log('[VAD] voiceGateEarly blocking, frames:', freqFrameCount); }
          rafId = setTimeout(tick, 30);
          return;
        }
        const ut = now - utteranceStartMs;
        const forceCut = ut >= VAD_MAX_UTTERANCE_MS;

        // Silence detection using both energy AND speech spectrum analysis.
        // Even if RMS is above threshold, if spectrum shows no speech pattern, treat as silence.
        const dynamicEnd = Math.max(VAD_END_RMS, vadNoiseFloor * 1.15);
        const specCheck = analyzeSpeechSpectrum(analyser, audioCtx.sampleRate);
        const isSpeechActive = smoothed >= dynamicEnd && specCheck.isSpeech;
        if (isSpeechActive) {
          silenceStartMs = null;
        } else if (silenceStartMs == null) {
          silenceStartMs = now;
        }

        // A long utterance is someone mid-sentence — give them the measured
        // 1600ms before deciding they are finished. See the note on
        // VAD_SILENCE_LONG_MS.
        const silenceNeeded = ut >= VAD_MIDSENTENCE_AFTER_MS ? VAD_SILENCE_LONG_MS : VAD_SILENCE_MS;
        const silenceDone =
          silenceStartMs != null &&
          now - silenceStartMs >= silenceNeeded &&
          ut >= VAD_MIN_UTTERANCE_MS;

        if (forceCut || silenceDone) {
          console.log('[VAD] ⏹ finalizing — silence:', silenceDone, 'waited:', silenceNeeded + 'ms', 'forceCut:', forceCut, 'duration:', ut, 'ms, rms:', smoothed.toFixed(4));
          finalizeRecording();
        }
      }

      rafId = setTimeout(tick, 30);
      } catch (tickErr) {
        rafId = setTimeout(tick, 30); // keep going despite error
      }
    };

    (async () => {
      try {
        // NOTE: Do NOT call requestMicrophoneAccess here — on macOS, askForMediaAccess
        // can SIGABRT. Permission is obtained naturally through getUserMedia → Chromium
        // media permission flow → macOS dialog.
        console.log('[VAD] Requesting getUserMedia...');
        const acquired = await navigator.mediaDevices.getUserMedia(getAudioConstraints());
        // Race-safe check: if the effect tore down (user muted, unmounted)
        // while getUserMedia was in flight, release the stream immediately.
        // Without this, the OS mic hardware (and indicator light) stay on
        // forever even though our React state says the mic is off.
        if (cancelled || !micOnRef.current) {
          console.log('[VAD] acquired stream but effect was cancelled / mic muted — releasing');
          try { acquired.getTracks().forEach((t) => { t.enabled = false; t.stop(); }); } catch (_) {}
          return;
        }
        stream = acquired;
        console.log('[VAD] getUserMedia SUCCESS — tracks:', stream.getAudioTracks().length);
        const activeTrack = stream.getAudioTracks ? stream.getAudioTracks()[0] : null;
        if (activeTrack?.label) { console.log('[VAD] Mic:', activeTrack.label); setActiveMicLabel(activeTrack.label); }
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.75;
        source.connect(analyser);
        vadPhase = 'idle';
        console.log('[VAD] ✅ VAD loop started');
        rafId = setTimeout(tick, 30);
      } catch (e) {
        console.error('[VAD] ❌ getUserMedia FAILED:', e.name, e.message);
        if (!cancelled) setVoiceError(microphoneDeniedMessageRef.current);
      }
    })();

    return () => {
      cancelled = true;
      setVoiceRecording(false);
      // PTT release: this cleanup fires when the user turns the mic OFF. If an
      // utterance is still in progress, FINALIZE it first (transcribe + send so
      // Aria replies) — closing the mic means "I'm done, send it," not "discard."
      // The recorder's buffered audio is flushed on stop() before its onstop, so
      // stopAll() right after doesn't lose it. finalizeRecording's onstop bypasses
      // the mute-drop because muteFinalize is set. Idempotent with the tick gate.
      if (vadPhase === 'recording' && mediaRecorder && mediaRecorder.state !== 'inactive' && recordChunks.length) {
        try { muteFinalize = true; finalizeRecording(); } catch (_) {}
      }
      stopAll();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks via stable refs to prevent teardown mid-recording
  }, [isDesktopApp, hasSpokenDesktopIntro, vadCanRun, effectiveSection, micOn, voiceEnrolling, introLocked, voicePolicy, getAudioConstraints]);

  // ── Effect 4: Auto-retry mic permission ──────────────────────────────────
  useEffect(() => {
    if (!micOn || !voiceError) return;
    const isDeniedErr = /denied|blocked|toggle/i.test(voiceError);
    if (!isDeniedErr) return;
    // Re-enable ONLY if the user hasn't muted in the meantime. micOnRef
    // is the synchronous mirror of the latest state.
    const reEnableIfUserDidntMute = () => {
      if (micOnRef.current === false) {
        console.log('[VAD] auto-retry: user muted during retry window — NOT re-enabling');
        return;
      }
      setMicOn(true);
    };
    const timer = setInterval(async () => {
      try {
        if (isDesktopApp && window.electronAPI?.getDeviceCapabilities) {
          const caps = await window.electronAPI.getDeviceCapabilities();
          const status = String(caps?.microphone || '').toLowerCase();
          if (status === 'granted' || status === 'authorized') {
            if (micOnRef.current === false) { clearInterval(timer); return; }
            setVoiceError('');
            setMicOn(false);
            setTimeout(reEnableIfUserDidntMute, 300);
            clearInterval(timer);
          }
        } else {
          const s = await navigator.mediaDevices.getUserMedia({ audio: true });
          s.getTracks().forEach((t) => t.stop());
          if (micOnRef.current === false) { clearInterval(timer); return; }
          setVoiceError('');
          setMicOn(false);
          setTimeout(reEnableIfUserDidntMute, 300);
          clearInterval(timer);
        }
      } catch (_) {}
    }, 3000);
    return () => clearInterval(timer);
  }, [micOn, voiceError, isDesktopApp]);

  // ── Return ───────────────────────────────────────────────────────────────
  return {
    voiceRecording,
    voiceListening,
    liveTranscript, setLiveTranscript,
    activeMicLabel,
    lastMicPeak, setLastMicPeak,
    voiceProfileEnrolled, setVoiceProfileEnrolled,
    voiceAdaptationCount, setVoiceAdaptationCount,
    voiceMatchStrict, setVoiceMatchStrict,
    voiceEnrollProgress, setVoiceEnrollProgress,
    micSpeaking,
    vadCanRun,
    recognitionRef,
    conversationContextRef,
  };
}

export { computeMicRms, analyzeSpeechSpectrum, pickMediaRecorderMime, VAD_SILENCE_MS, VAD_MIN_UTTERANCE_MS, VAD_MAX_UTTERANCE_MS, VAD_START_RMS, VAD_END_RMS, VAD_COOLDOWN_MS, TTS_TAIL_GATE_MS, MIC_SPEAKING_RMS_THRESHOLD };
